import { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { operatorLabels } from "./config";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../ui/select";
import styles from "./styles.module.css";
import Button from "../../button-v1";
import Input from "../../ui/input";

const FilterBuilder = ({ fieldConfig, initialFilters = [], onChange }) => {
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    onChange?.(filters);
  }, [filters, onChange]);

  const addFilter = () => {
    const selectedFieldValues = filters.map((f) => f.field);
    const availableField = fieldConfig.find(
      (f) => !selectedFieldValues.includes(f.value)
    );
    if (!availableField) return; // No available fields left
    const newFilter = {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: availableField.value,
      operator: availableField.operators[0],
      value: "",
    };
    setFilters([...filters, newFilter]);
  };

  const removeFilter = (id) => {
    setFilters(filters.filter((filter) => filter.id !== id));
  };

  const updateFilter = (id, updates) => {
    setFilters(
      filters.map((filter) => {
        if (filter.id === id) {
          const updatedFilter = { ...filter, ...updates };

          if (updates.field && updates.field !== filter.field) {
            const newFieldConfig = fieldConfig.find(
              (f) => f.value === updates.field
            );
            if (newFieldConfig) {
              updatedFilter.operator = newFieldConfig.operators[0];
              updatedFilter.value = "";
            }
          }

          return updatedFilter;
        }
        return filter;
      })
    );
  };

  const getFieldConfig = (fieldValue) => {
    return fieldConfig.find((field) => field.value === fieldValue);
  };

  const renderValueInput = (filter) => {
    const field = getFieldConfig(filter.field);
    if (!field) return null;

    // Operators that don't need value input
    const noValueOperators = [
      "is_empty",
      "is_not_empty",
      "is_true",
      "is_false",
    ];
    if (noValueOperators.includes(filter.operator)) {
      return null;
    }

    switch (field.type) {
      case "text":
        return (
          <Input
            type="text"
            // className={`${styles.input} ${styles.valueInput}`}
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
            placeholder="Enter value..."
          />
        );

      case "number":
        return (
          <Input
            type="number"
            // className={`${styles.input} ${styles.valueInput}`}
            value={filter.value}
            onChange={(e) =>
              updateFilter(filter.id, {
                value: parseFloat(e.target.value) || 0,
              })
            }
            placeholder="Enter number..."
          />
        );

      case "date":
        return (
          <Input
            type="date"
            // className={`${styles.input} ${styles.valueInput}`}
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
          />
        );

      case "boolean":
        return (
          <Input
            type="checkbox"
            // className={styles.checkbox}
            checked={filter.value}
            onChange={(e) =>
              updateFilter(filter.id, { value: e.target.checked })
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.filterBuilder}>
      {/* <div className={styles.header}>
        <h2 className={styles.title}>Filters</h2>
      </div> */}

      {filters.length === 0 ? (
        <div className={styles.emptyState}>
          {/* <p>No filters applied yet</p> */}
          <Button variant="ghost" onClick={addFilter}>
            <Plus size={16} />
            Add Filter
          </Button>
        </div>
      ) : (
        <section className={styles.filter_container}>
          <div className={styles.filterList}>
            {filters.map((filter) => {
              const field = getFieldConfig(filter.field);
              const selectedFieldValues = filters
                .filter((f) => f.id !== filter.id)
                .map((f) => f.field);
              return (
                <div key={filter.id} className={styles.filterRow}>
                  <Select
                    value={filter.field}
                    onChange={(newValue) =>
                      updateFilter(filter.id, { field: newValue })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {fieldConfig.find((f) => f.value === filter.field)
                          ?.label || "Select"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                    // className={`${styles.select} ${styles.fieldSelect}`}
                    >
                      {fieldConfig
                        .filter(
                          (f) =>
                            f.value === filter.field ||
                            !selectedFieldValues.includes(f.value)
                        )
                        .map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            {f.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filter.operator}
                    onChange={(newValue) =>
                      updateFilter(filter.id, { operator: newValue })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {operatorLabels[filter.operator]}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent
                    // className={`${styles.select} ${styles.operatorSelect}`}
                    >
                      {field?.operators.map((operator) => (
                        <SelectItem key={operator} value={operator}>
                          {operatorLabels[operator]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {renderValueInput(filter)}

                  <button
                    className={styles.removeButton}
                    onClick={() => removeFilter(filter.id)}
                    title="Remove filter"
                  >
                    <X size={16} />
                  </button>
                </div>
              );
            })}
          </div>

          <section className={styles.filter_actions}>
            {filters.length > 0 && (
              <Button variant="ghost" onClick={() => setFilters([])}>
                Clear Filters
              </Button>
            )}
            <Button
              onClick={addFilter}
              disabled={filters.length >= fieldConfig.length}
            >
              <Plus size={16} />
              Add Filter
            </Button>
          </section>
        </section>
      )}
    </div>
  );
};

export default FilterBuilder;
